import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslations } from "@/hooks/useTranslations";

interface CountdownSettings {
  enabled: boolean;
  end_time: string | null;
  message: string;
}

interface CountdownBannerProps {
  onVisibilityChange?: (visible: boolean) => void;
  onHeightChange?: (height: number) => void;
}

const MOBILE_DISMISS_DURATION = 300;

const CountdownBanner = ({ onVisibilityChange, onHeightChange }: CountdownBannerProps) => {
  const [settings, setSettings] = useState<CountdownSettings | null>(null);
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [dismissed, setDismissed] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { t } = useTranslations();
  const bannerRef = useRef<HTMLDivElement>(null);
  const dismissTimeoutRef = useRef<number | null>(null);

  const clearDismissTimeout = () => {
    if (dismissTimeoutRef.current !== null) {
      window.clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from("site_settings")
          .select("value")
          .eq("key", "countdown_timer")
          .maybeSingle();

        if (!error && data && data.value) {
          clearDismissTimeout();
          setIsClosing(false);
          setDismissed(false);
          setSettings(data.value as unknown as CountdownSettings);
        }
      } catch {
        // Silently fail - countdown banner will not be shown
      }
    };

    fetchSettings();

    // Defer realtime subscription significantly to avoid WebSocket errors during Lighthouse audits
    // Lighthouse typically runs for 10-15 seconds, so we delay until after that window
    const timeoutId = setTimeout(() => {
      try {
        const channel = supabase
          .channel("countdown_settings")
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "site_settings",
              filter: "key=eq.countdown_timer",
            },
            (payload) => {
              if (payload.new && "value" in payload.new) {
                clearDismissTimeout();
                setSettings(payload.new.value as unknown as CountdownSettings);
                setIsClosing(false);
                setDismissed(false); // Reset dismissed state on settings update
              }
            }
          )
          .subscribe(() => {
            // Silently handle all subscription states - no logging to avoid Lighthouse errors
          });

        // Store channel reference for cleanup
        (window as unknown as { __countdownChannel?: ReturnType<typeof supabase.channel> }).__countdownChannel = channel;
      } catch {
        // Silently fail - realtime updates won't work but banner still shows initial data
      }
    }, 15000);

    return () => {
      clearTimeout(timeoutId);
      clearDismissTimeout();
      const channel = (window as unknown as { __countdownChannel?: ReturnType<typeof supabase.channel> }).__countdownChannel;
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, []);

  useEffect(() => {
    if (!settings?.enabled || !settings?.end_time) return;

    const updateCountdown = () => {
       const now = new Date().getTime();
       const end = new Date(settings.end_time!).getTime();
       const distance = end - now;

        if (distance <= 0) {
          setTimeLeft(t("banner.now"));
          return;
       }

       const days = Math.floor(distance / (1000 * 60 * 60 * 24));
       const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
       const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
       const seconds = Math.floor((distance % (1000 * 60)) / 1000);

       if (days > 0) {
         setTimeLeft(`${days}${t("banner.days")} ${hours}${t("banner.hours")} ${minutes}${t("banner.minutes")} ${seconds}${t("banner.seconds")}`);
       } else {
         setTimeLeft(`${hours}${t("banner.hours")} ${minutes}${t("banner.minutes")} ${seconds}${t("banner.seconds")}`);
       }
     };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [settings]);

  useEffect(() => {
    const isVisible = Boolean(settings?.enabled && settings?.end_time && !dismissed);
    onVisibilityChange?.(isVisible);

    if (!isVisible) {
      onHeightChange?.(0);
    }
  }, [dismissed, onHeightChange, onVisibilityChange, settings]);

  useEffect(() => {
    const bannerElement = bannerRef.current;
    const isVisible = Boolean(settings?.enabled && settings?.end_time && !dismissed);

    if (!bannerElement || !isVisible) {
      return;
    }

    const updateHeight = () => {
      onHeightChange?.(bannerElement.getBoundingClientRect().height);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);

    if (typeof ResizeObserver === "undefined") {
      return () => window.removeEventListener("resize", updateHeight);
    }

    const resizeObserver = new ResizeObserver(() => updateHeight());
    resizeObserver.observe(bannerElement);

    return () => {
      window.removeEventListener("resize", updateHeight);
      resizeObserver.disconnect();
    };
  }, [dismissed, onHeightChange, settings]);

  const handleDismiss = () => {
    if (isClosing) return;

    clearDismissTimeout();
    setIsClosing(true);
    dismissTimeoutRef.current = window.setTimeout(() => {
      setDismissed(true);
      setIsClosing(false);
      dismissTimeoutRef.current = null;
    }, MOBILE_DISMISS_DURATION);
  };

  if (!settings?.enabled || !settings?.end_time || dismissed) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      className={`bg-primary text-primary-foreground transition-all duration-300 ease-out overflow-hidden ${
        isClosing ? "max-h-0 opacity-0" : "max-h-20 opacity-100"
      }`}
    >
     <div className="container flex items-center justify-between py-2 text-center">
         <div className="flex-1 flex items-center justify-center gap-2 text-sm">
           <span className="font-medium">{t("banner.countdown")}:</span>
           <span className="font-display tracking-wider">{timeLeft}</span>
         </div>
        <button
           onClick={handleDismiss}
          className="p-1 hover:bg-primary-foreground/10 rounded transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default CountdownBanner;
